import { forwardRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Typography as MuiTypography, TypographyProps as MuiTypographyProps } from '@mui/material';
import { clsx } from 'clsx';
import { useFeatureFlags } from '@chronosphereio/core';
import { EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME } from '../DataTable';

const BOLD_VARIANTS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function isBoldVariant(variant?: string) {
  return variant !== undefined && BOLD_VARIANTS.has(variant);
}

export type TypographyProps = MuiTypographyProps & {
  // We need to add `component` specifically here because it's polymorphic in the underlying MUI component and does not
  // play well with the generic forwardRef type. https://mui.com/material-ui/guides/composition/#with-typescript
  /**
   * Override property for the component or html tag that renders the Typography text.
   *
   * Defaults to `div` tag for `'body1'` and `'body2'` variants and if no variant is provided
   *
   * Defaults to `span` tag for `'caption'`, `'code'`, `'button'`, `'overline1'`, and `'overline2'` variants
   */
  component?: React.ElementType;

  /**
   * Set to true when the typography is being rendered inside a `DataTable` cell with `enableExpandableCells` when
   * you want the text to ellipsize at rest and wrap on hover.
   */
  wrapInExpandableTableCell?: boolean;
};

export const Typography = forwardRef<HTMLSpanElement, TypographyProps>(function Typography(
  { wrapInExpandableTableCell, className: classNameProp, ...props },
  ref
) {
  const { panwRebrandStubYear } = useFeatureFlags();

  const className = clsx(classNameProp, wrapInExpandableTableCell && EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME);

  // Only assign a default component if one is not set by the caller
  let component = props.component;
  if (component === undefined) {
    // If no variant is provided, default to a 'div' tag because 'p' tags can't be nested inside themselves
    if (props.variant === undefined) {
      component = 'div';
    }

    // For body variants, use a 'div' tag instead of 'p' to avoid accidental invalid DOM nesting
    if (props.variant?.startsWith('body')) {
      component = 'div';
    }

    // For inline variants, use a 'span' tag instead of 'p' to avoid accidental invalid DOM nesting
    if (props.variant && ['caption', 'code', 'button', 'overline1', 'overline2'].includes(props.variant)) {
      component = 'span';
    }
  }

  if (panwRebrandStubYear && !props.color) {
    if (isBoldVariant(props.variant)) {
      props.color = 'text.bold';
    }
  }

  return <MuiTypography component={component} ref={ref} className={className} {...props} />;
});
